import {ColorPicker, Form, Input, InputNumber, Tag, Typography} from 'antd';
import type {FormInstance} from 'antd';
import React from 'react';
import BandPill from '../../../components/common/BandPill';
import {useTranslation} from '../../../hooks/useTranslation';
import {isFormulaValid} from '../../../utils/formulaEngine';
import {IMPACT_COLOR_PRESETS} from './impactColors';

const {Text} = Typography;

export interface ScaleBandFormValues {
  label: string;
  color: string;
  displayOrder?: number | null;
  formula?: string | null;
}

interface ScaleBandFormProps {
  form: FormInstance<ScaleBandFormValues>;
  initialValues?: Partial<ScaleBandFormValues>;
}

const ScaleBandForm: React.FC<ScaleBandFormProps> = ({form, initialValues}) => {
  const {t} = useTranslation();

  const presetsLabel = t('settings.content.impactAssessment.formulaTable.columns.description');
  const requiredMessage = t('messages.notFound');

  const watchedLabel = Form.useWatch('label', form);
  const watchedColor = Form.useWatch('color', form);
  const watchedOrder = Form.useWatch('displayOrder', form);

  const previewBand = {
    label: watchedLabel || t('settings.content.impactAssessment.formulaTable.columns.description'),
    color: watchedColor || IMPACT_COLOR_PRESETS[0],
    displayOrder: typeof watchedOrder === 'number' ? watchedOrder : null,
  };


  return (
    <Form
      form={form}
      layout="vertical"
      preserve={false}
      initialValues={initialValues}
    >
      <div className="gt-ac-drawer-preview">
        <Text className="gt-ac-drawer-preview-label">
          {t('settings.content.impactAssessment.scaleDrawer.previewLabel')}
        </Text>
        <BandPill band={previewBand}/>
      </div>

      <Form.Item
        name="label"
        label={t('settings.content.impactAssessment.formulaTable.columns.description')}
        rules={[{required: true, message: requiredMessage}]}
      >
        <Input/>
      </Form.Item>
      <Form.Item
        name="color"
        label={t('settings.content.impactAssessment.formulaTable.columns.color')}
        getValueFromEvent={(color: any) =>
          typeof color === 'string' ? color : color?.toHexString?.() ?? ''
        }
        rules={[{required: true, message: requiredMessage}]}
      >
        <ColorPicker
          format="hex"
          showText
          presets={[
            {
              label: presetsLabel,
              colors: IMPACT_COLOR_PRESETS,
            },
          ]}
        />
      </Form.Item>
      <Form.Item
        name="displayOrder"
        label={t('settings.content.impactAssessment.formulaTable.columns.displayOrder')}
      >
        <InputNumber min={0} style={{width: '100%'}}/>
      </Form.Item>
      <Form.Item
        name="formula"
        label={t('settings.content.impactAssessment.formulaTable.columns.formula')}
        rules={[
          {
            validator: (_, value: string) =>
              isFormulaValid(value)
                ? Promise.resolve()
                : Promise.reject(
                    new Error(t('settings.content.impactAssessment.scaleDrawer.formulaInvalid'))
                  ),
          },
        ]}
      >
        <Input.TextArea
          rows={2}
          className="gt-ac-formula-input"
          placeholder={t('settings.content.impactAssessment.scaleDrawer.formulaPlaceholder')}
        />
      </Form.Item>
    </Form>
  );
};

export default ScaleBandForm;
